import { useEffect, useRef } from 'react';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import useApiQuery from 'lib/api/useApiQuery';
import {
  agentCallReviewsFilesRequest,
  callReviewsKeys,
  AGENT_FILES_PAGE_SIZE
} from '../api/callReviewsFiles';

/**
 * loads one agent's files for the admin drill-down. caching handles what the table
 * needs on its own: the request only fires once expanded, re-expanding shows the
 * cached rows while revalidating behind them, and responses that no longer match
 * the visible page are dropped because they belong to a different query key.
 * @param agentId - zoho agent id of the expanded row
 * @param page - 1-indexed page of that agent's files
 * @param enabled - false while the row is collapsed
 */
export default function useAgentCallReviewsFiles(
  agentId,
  page,
  enabled = true
) {
  const queryClient = useQueryClient();

  const query = useApiQuery(
    callReviewsKeys.agent(agentId, page, AGENT_FILES_PAGE_SIZE),
    agentCallReviewsFilesRequest(agentId, {
      page,
      pageSize: AGENT_FILES_PAGE_SIZE
    }),
    {
      enabled: enabled && Boolean(agentId),
      placeholderData: keepPreviousData
    }
  );

  // a 404 means the roster still lists an agent the api no longer serves, so the
  // roster itself is stale. only the overview queries are invalidated — invalidating
  // everything would refetch this failing query too.
  const isMissingAgent = Boolean(query.error?.isNotFound);
  useEffect(() => {
    if (!isMissingAgent) return;
    queryClient.invalidateQueries({
      queryKey: callReviewsKeys.overviewAll()
    });
  }, [isMissingAgent, queryClient]);

  // react-query's own placeholder (keepPreviousData) only bridges the gap while
  // the new query key is still pending — once a key settles into error, data goes
  // back to undefined and the placeholder is gone for good. page is part of the
  // key here, so paging within an expanded row while the api is down would
  // otherwise blank that agent's table instead of just failing to refresh it.
  const lastGoodDataRef = useRef(null);
  if (query.isSuccess) lastGoodDataRef.current = query.data;

  const data = isMissingAgent ? null : query.data ?? lastGoodDataRef.current;

  return {
    files: data?.items ?? [],
    agent: data?.agent ?? null,
    total: data?.total ?? 0,
    totalPages: data?.total_pages ?? 0,
    isLoading: enabled && query.isPending && !data,
    // a fetch with rows already on screen: paging, revalidating, or retrying
    isRefreshing: query.isFetching && !query.isPending,
    // failureCount only climbs while the shared retry policy is still trying
    isRetrying: query.isFetching && query.failureCount > 0,
    // a missing agent renders as an empty table, not as an error. a real error
    // only blanks the panel when there was never any data to fall back to —
    // AgentFilesPanel already checks `hasError && files.length === 0`
    hasError: query.isError && !isMissingAgent,
    refetch: query.refetch
  };
}
